import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { HeroLayout1 } from '@components/hero/components/hero-layout-1/model';
import { HeroMenuService } from '@components/hero/components/hero-layout-1/services';
import { LogsService } from '@services/logs';
import { PageTypeService } from '@services/page-type';
import { ResponsiveService } from '@services/responsive';
import { ImageComponent } from '@shared/components/image';
import { SocialNetworksComponent } from '@shared/components/social-networks';
import { socialNetworksStyle } from '@shared/components/social-networks/config';
import { SocialNetworkTheme } from '@shared/components/social-networks/enum';
import { SocialNetworksItem, SocialNetworksStyle } from '@shared/components/social-networks/model';
import { TitleComponent } from '@shared/components/title';
import { Title as TitleModel } from '@shared/components/title/model';
import { VideoComponent } from '@shared/components/video';
import { IconsDirective } from '@shared/directives';
import { toBasePageType } from '@shared/utils';
import { Subject, takeUntil } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-hero-layout-1',
  imports: [
    AsyncPipe,
    IconsDirective,
    NgTemplateOutlet,
    TitleComponent,
    SocialNetworksComponent,
    VideoComponent,
    ImageComponent,
  ],
  templateUrl: './hero-layout-1.component.html',
  styleUrl: './hero-layout-1.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroLayout1Component implements OnInit, OnDestroy {
  readonly data = input.required<HeroLayout1>();
  menu$ = inject(HeroMenuService).getMenu$();
  title = signal<TitleModel>(null!);
  subtitle = signal<TitleModel>(null!);
  socialNetworks = signal<SocialNetworksItem[]>(null!);
  readonly socialNetworksStyle = signal<SocialNetworksStyle>(socialNetworksStyle[SocialNetworkTheme.GREY_SOCIAL]!);
  readonly breakpoints = inject(ResponsiveService).breakpoints;
  readonly #document = inject<Document>(DOCUMENT);
  readonly #pageTypeService = inject(PageTypeService);
  readonly #titleService = inject(Title);
  readonly #logsService = inject(LogsService);
  readonly #isOffsetAnchor = signal<boolean>(null!);
  readonly #window = this.#document.defaultView as Window;
  readonly #destroy$: Subject<boolean> = new Subject<boolean>();

  ngOnInit(): void {
    const { isMenu } = this.data();
    this.#pageTypeService
      .getPageType$()
      .pipe(
        takeUntil(this.#destroy$),
        tap((pageType) => {
          if (toBasePageType(pageType) === 'projects_details') {
            this.#isOffsetAnchor.set(true);
          }
          if (pageType === 'article') {
            if (isMenu) {
              this.#logsService.debug(
                'HeroLayout1Component',
                'Social networks hidden: menu is enabled on this component'
              );
            } else {
              this.socialNetworks.set([
                {
                  url: `https://www.facebook.com/sharer/sharer.php?u=${this.#window.location.href}`,
                  icon: 'S_FACEBOOK',
                },
                {
                  url: `https://twitter.com/intent/tweet?text=${encodeURI(this.#titleService.getTitle())} - ${
                    this.#window.location.href
                  }`,
                  icon: 'S_X_TWITTER',
                },
                {
                  url: `http://pinterest.com/pin/create/button/?url=${this.#window.location.href}`,
                  icon: 'S_PINTEREST',
                },
              ]);
            }
          }
        })
      )
      .subscribe();
    this.title.set({
      level: 'H1',
      text: this.data().title,
      color: 'BLACK',
      align: {
        mobile: 'LEFT',
        desktop: 'LEFT',
      },
      size: {
        mobile: 'H1',
        desktop: 'H3',
      },
    });
    if (this.data().subtitle) {
      this.subtitle.set({
        level: 'SPAN',
        type: 'LINE',
        font: 'INTER',
        color: 'GREY_LIGHT',
        text: this.data().subtitle!,
        align: {
          mobile: 'LEFT',
          desktop: 'LEFT',
        },
        size: {
          mobile: 'SPAN',
          desktop: 'SPAN',
        },
      });
    }
  }

  goToSection(anchor: string) {
    const element = this.#document.getElementById(anchor);
    if (element) {
      const rect = element.getBoundingClientRect();
      const offset = this.#isOffsetAnchor() ? this.#window.innerHeight / 2 : 0;
      const scrollOptions: ScrollToOptions = {
        left: rect.left + this.#window.scrollX,
        top: rect.top + this.#window.scrollY - offset,
        behavior: 'smooth',
      };

      this.#window.scrollTo(scrollOptions);
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next(true);
    this.#destroy$.complete();
  }
}
